import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { goodsReceipts, type GoodsReceiptListItem } from '@/api/goodsReceipts';
import { contractors as contractorsApi, orgs } from '@/api/catalogs';
import { taxInvoicesIncoming } from '@/api/taxInvoices';
import type { ColDef } from 'ag-grid-community';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';
import { agOrgCol, useOrgNames } from '@/shared/hooks/useOrgNames';
import DocListFilters, { ContractorFilter, DocListFiltersRow, NomenclatureFilter, useDocListFilters } from '@/shared/components/DocListFilters';
import { everVatPayer } from '@/shared/utils/vatFlags';

const fmt = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function GoodsReceiptsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // 2026-07-31 (рішення власника): стандартні фільтри списків (орг +
  // статус + період) + пошуковий фільтр за постачальником.
  const f = useDocListFilters();
  const [contractorFilter, setContractorFilter] = useState('');
  // 2026-08-11 (рішення власника): «хто продав товар X» — фільтр за
  // номенклатурою (EXISTS по рядках).
  const [nomFilter, setNomFilter] = useState('');
  // 2026-08-04 (рішення власника): контроль «чи отримав приход свою вхідну
  // ПН» — колонка + фільтр, ТІЛЬКИ для організацій-платників ПДВ.
  const [pnFilter, setPnFilter] = useState<'' | 'with' | 'without'>('');
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const showPnCol = useMemo(() => {
    const list = orgListQ.data?.items ?? [];
    if (f.orgId) return everVatPayer(list.find((o) => o.id === f.orgId));
    return list.some((o) => everVatPayer(o));
  }, [orgListQ.data, f.orgId]);

  const orgNames = useOrgNames();
  const refreshRef = useRef<(() => void) | null>(null);
  const { datasource, total } = useInfiniteGridDatasource<GoodsReceiptListItem>(
    (limit, offset, sort) => goodsReceipts.list({
      limit, offset,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      organization_id: f.orgId || undefined,
      contractor_id: contractorFilter || undefined,
      nomenclature_id: nomFilter || undefined,
      posted: f.status === 'posted' ? true : f.status === 'draft' ? false : undefined,
      date_from: f.dateFrom, date_to: f.dateTo,
      pn_link: pnFilter || undefined,
    }),
    [contractorFilter, nomFilter, pnFilter, ...f.queryKey],
    { attachmentsType: 'doc.goods_receipts' },
  );
  const refreshGrid = () => refreshRef.current?.();
  // Контроль підписання (0429): колонка «Підпис» + фон непідписаних.
  const sc = useSignControlInfinite<GoodsReceiptListItem>('doc.goods_receipts', f.orgId);

  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });

  const contractorName = useMemo(() => {
    const map = new Map<string, string>();
    contractorsQ.data?.items.forEach((c) => map.set(c.id, c.name));
    return map;
  }, [contractorsQ.data]);

  // Ручна привʼязка ПН до приходу (авто-матчинг за сумою міг не знайти —
  // часткові поставки/одна ПН на кілька приходів).
  const [linkFor, setLinkFor] = useState<GoodsReceiptListItem | null>(null);
  const unlinkedPnQ = useQuery({
    queryKey: ['unlinked-pns', linkFor?.contractor_id, linkFor?.organization_id],
    queryFn: () => taxInvoicesIncoming.list({
      organization_id: linkFor!.organization_id,
      contractor_id: linkFor!.contractor_id, limit: 100,
    }),
    enabled: !!linkFor,
  });
  const bind = useMutation({
    mutationFn: (pnId: string) => taxInvoicesIncoming.update(pnId, {
      base_document_type: 'doc.goods_receipts',
      base_document_id: linkFor!.id,
    }),
    onSuccess: () => {
      setLinkFor(null);
      refreshGrid();
      qc.invalidateQueries({ queryKey: ['tax-invoices-incoming'] });
    },
  });

  const cols = useMemo<ColDef<GoodsReceiptListItem>[]>(() => {
    const base: ColDef<GoodsReceiptListItem>[] = [
      {
        headerName: '', width: 44, sortable: false,
        cellRenderer: (p: { data?: GoodsReceiptListItem }) =>
          <ImportedBadge source={p.data?.external_source} />,
      },
      { field: 'document_number', headerName: t('gr.number'), width: 150, sortable: true },
      {
        field: 'document_date', headerName: t('gr.date'), width: 170, sortable: true,
        valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString(i18n.language) : '',
      },
      agOrgCol(orgNames),
      {
        field: 'contractor_id', headerName: t('gr.contractor'), flex: 1, minWidth: 220, sortable: true,
        valueFormatter: (p) => contractorName.get(p.value) ?? '',
      },
      {
        field: 'total_amount', headerName: t('gr.total'), width: 140, sortable: true,
        cellStyle: { textAlign: 'right' },
        valueFormatter: (p) => p.value != null ? fmt(p.value) : '',
      },
      {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) =>
        p.data?._att ? '✓' : '',
    },
    {
        field: 'currency_code', headerName: 'Валюта', width: 90, sortable: false,
        cellStyle: { textAlign: 'center' },
        cellRenderer: (p: { data?: GoodsReceiptListItem }) =>
          p.data?.currency_code && p.data.currency_code !== 'UAH'
            ? <span style={{ color: 'var(--badge-blue-fg)', fontWeight: 700 }}>{p.data.currency_code}</span>
            : (p.data ? 'UAH' : ''),
      },
    ];
    if (showPnCol) {
      base.push({
        headerName: 'ПН', width: 160, sortable: false,
        cellStyle: { textAlign: 'center' },
        cellRenderer: (p: { data?: GoodsReceiptListItem }) => {
          const r = p.data;
          if (!r) return '';
          if (!r.contractor_is_vat_payer) {
            return <span style={{ color: '#94a3b8' }}
              title="Постачальник не платник ПДВ — ПН не очікується">—</span>;
          }
          if (r.pn_id) {
            const regDate = r.pn_registration_date
              ? ` ${new Date(r.pn_registration_date).toLocaleDateString('uk-UA')}` : '';
            const isErpn = r.pn_external_source === 'erpn';
            return (
              <a style={{ cursor: 'pointer', color: 'var(--badge-green-fg)',
                fontWeight: isErpn ? 600 : 400 }}
                title={isErpn
                  ? `ПН №${r.pn_number} — підтверджено випискою ЄРПН${regDate ? `, реєстрація${regDate}` : ''}. Відкрити ПН`
                  : `ПН №${r.pn_number} — створена вручну${r.pn_is_registered ? ', зареєстрована зі слів постачальника' : ', НЕ зареєстрована'}. Відкрити ПН`}
                onClick={(e) => { e.stopPropagation(); navigate(`/tax-invoices-incoming/${r.pn_id}`); }}>
                {isErpn ? `✓ ЄРПН${regDate}` : `✓ №${r.pn_number}`}
              </a>
            );
          }
          return (
            <span style={{ whiteSpace: 'nowrap' }}>
              <span style={{ color: '#dc2626', fontWeight: 600 }}
                title="Вхідної ПН немає — без неї права на податковий кредит немає. Потягніть ПН з ЄРПН (Кабінет ДПС → Отримати ПН) або привʼяжіть вручну.">
                ⚠ немає
              </span>
              {' '}
              <a style={{ cursor: 'pointer', color: 'var(--primary)', fontSize: '0.82rem' }}
                title="Привʼязати наявну вхідну ПН цього постачальника"
                onClick={(e) => { e.stopPropagation(); setLinkFor(r); }}>
                привʼязати
              </a>
            </span>
          );
        },
      });
    }
    base.push({
      field: 'posted', headerName: t('gr.posted'), width: 130, sortable: true,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    });
    return base;
  }, [t, i18n.language, contractorName, orgNames, showPnCol, navigate]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>
          {t('gr.title')}
          {total !== null && (
            <span style={{ fontSize: 13, color: 'var(--muted)', marginLeft: 8, fontWeight: 400 }}>
              ({total})
            </span>
          )}
        </h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/goods-receipts/new')}>
          + {t('gr.new')}
        </button>
      </div>
      <DocListFiltersRow>
        <DocListFilters f={f} statusOptions={[
          { value: '', label: 'Усі статуси' },
          { value: 'posted', label: 'Проведені' },
          { value: 'draft', label: 'Чернетки' },
        ]} />
        <ContractorFilter value={contractorFilter} onChange={setContractorFilter}
          label="постачальник" />
        <NomenclatureFilter value={nomFilter} onChange={setNomFilter} />
        {showPnCol && (
          <select value={pnFilter} style={{ width: 150, flex: '0 0 auto' }}
            title="Контроль вхідних ПН: «без ПН» показує лише приходи від платників ПДВ без привʼязаної ПН"
            onChange={(e) => setPnFilter(e.target.value as '' | 'with' | 'without')}>
            <option value="">ПН: усі</option>
            <option value="without">без ПН</option>
            <option value="with">з ПН</option>
          </select>
        )}
      </DocListFiltersRow>
      <InfiniteDocGrid<GoodsReceiptListItem>
        copyRow={(r) => navigate(`/goods-receipts/new?copy=${r.id}`)}
        deleteRow={{ remove: (id) => goodsReceipts.remove(id) }}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="goods-receipts"
        refreshRef={refreshRef}
        onRowDoubleClicked={(r) => navigate(`/goods-receipts/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark
          ? { opacity: 0.4 } : sc.getRowStyle(p)}
      />

      {linkFor && (
        <div style={{
          position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 60,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setLinkFor(null)}>
          <div style={{
            background: 'var(--bg-panel)', borderRadius: 10, padding: '1rem',
            width: 640, maxHeight: '75vh', overflow: 'auto',
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>
                Привʼязати ПН до приходу №{linkFor.document_number} ({fmt(linkFor.total_amount)} грн)
              </h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setLinkFor(null)}>закрити</button>
            </div>
            {unlinkedPnQ.isLoading && <div>Завантаження…</div>}
            {(() => {
              const free = (unlinkedPnQ.data?.items ?? [])
                .filter((p) => !p.base_document_id && !p.deletion_mark);
              if (unlinkedPnQ.isLoading) return null;
              if (!free.length) {
                return (
                  <div style={{ color: 'var(--text-secondary)' }}>
                    Непривʼязаних ПН цього постачальника немає. Потягніть ПН з
                    ЄРПН: Кабінет ДПС → «Отримати ПН».
                  </div>
                );
              }
              return (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-soft-2)', textAlign: 'left' }}>
                      <th style={{ padding: '0.4rem' }}>№ ПН</th>
                      <th style={{ padding: '0.4rem' }}>Дата</th>
                      <th style={{ padding: '0.4rem', textAlign: 'right' }}>Сума</th>
                      <th style={{ padding: '0.4rem' }}>ЄРПН</th>
                      <th style={{ padding: '0.4rem' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {free.map((p) => (
                      <tr key={p.id} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '0.4rem' }}>{p.invoice_number}</td>
                        <td style={{ padding: '0.4rem' }}>
                          {new Date(p.invoice_date).toLocaleDateString('uk-UA')}
                        </td>
                        <td style={{ padding: '0.4rem', textAlign: 'right' }}>{fmt(p.total_amount)}</td>
                        <td style={{ padding: '0.4rem' }}>
                          {p.external_source === 'erpn' ? '✓ ЄРПН'
                            : p.is_registered_erpn ? '✓ вручну' : '✗'}
                        </td>
                        <td style={{ padding: '0.4rem' }}>
                          <button disabled={bind.isPending}
                            onClick={() => bind.mutate(p.id)}>обрати</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
